import { useQuery } from "@tanstack/react-query";
import { LuArrowLeft } from "react-icons/lu";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { BeatLoader } from "react-spinners";

import { getUserById } from "../../services/userService";
import PersonalDetails from "../../components/users/personal-details";
import EmployerDetails from "../../components/users/employer-details";
import { IEmployer, IProfessional } from "../../types";
import ProfessionalDetails from "../../components/users/professional-details";
import AboutDetails from "../../components/users/about-details";
import SkillsDetails from "../../components/users/skills-details";
import { UserActions } from "../../components/users/actions";

const UserDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data: userData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["user", id],
    queryFn: () => getUserById(id as string),
    enabled: !!id,
  });

  const referrer = location.state?.referrer || "/users/all";

  const handleBack = () => {
    navigate(referrer);
  };

  if (isLoading)
    return (
      <div className="p-4">
        <BeatLoader color="#3B82F6" />
      </div>
    );
  if (error) return <p className="text-red-500">Error fetching user data.</p>;

  const user = userData?.user;
  const userInfo = userData?.userInfo;
  const LuArrowLeftIcon = LuArrowLeft as React.ElementType;

  return (
    <div className="p-2 md:py-8 md:px-8 space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="rounded-full w-[80px] h-[80px] md:w-[80px] md:h-[80px] flex items-center justify-center shadow-sm">
            <img
              className="rounded-full h-full w-full object-cover"
              src={user?.image}
              alt="User Avatar"
            />
          </div>
          <div>
            <p className="text-xl font-semibold leading-none">
              {user?.lastName} {user?.firstName}
            </p>
            <p className="text-gray-500 text-sm">{user?.profession}</p>
          </div>
        </div>
        <div
          onClick={handleBack}
          className="p-2 cursor-pointer bg-indigo-800 rounded-full flex items-center justify-center text-white hover:bg-opacity-50 transition"
        >
          <LuArrowLeftIcon className="text-lg" />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-4 gap-y-4">
        {user && <PersonalDetails userProfile={user} />}
        {user?.role === "employer" && (
          <EmployerDetails employer={userInfo as IEmployer} />
        )}
        {user?.role === "professional" && (
          <ProfessionalDetails professional={userInfo as IProfessional} />
        )}

        {user?.role === "professional" && (
          <AboutDetails professional={userInfo as IProfessional} />
        )}
        {user?.role === "professional" && userInfo && "skills" in userInfo && (
          <SkillsDetails skills={userInfo.skills} />
        )}
        {user && <UserActions user={user} />}
      </div>
    </div>
  );
};

export default UserDetails;
